import classNames from 'classnames';
import React, {CSSProperties, ImgHTMLAttributes, ReactElement} from 'react';
import ElementLoadingSkeleton from '../loadingSkeleton/ElementLoadingSkeleton';
import {useMediaLoad} from './useMediaLoad';
import './media.scss';

interface MediaImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError' | 'onLoad'> {
    src?: string;
    fallbackSrc: string;
    maxRetries?: number;
    wrapperClassName?: string;
    wrapperStyle?: CSSProperties;
}

function MediaImage(props: MediaImageProps): ReactElement {
    const {src, fallbackSrc, maxRetries, wrapperClassName, wrapperStyle, className, style, alt = '', ...imgProps} = props;
    const {effectiveSrc, handleLoad, handleError, isLoading, hasFailed} = useMediaLoad({
        src,
        maxRetries,
    });

    const showFallback = hasFailed || !src;
    const imageSrc = showFallback ? fallbackSrc : effectiveSrc;

    return (
        <figure className={classNames('c-media', wrapperClassName)} style={wrapperStyle}>
            {isLoading && <ElementLoadingSkeleton />}
            {imageSrc && (
                <img
                    {...imgProps}
                    alt={alt}
                    className={className}
                    data-testid="media-image"
                    src={imageSrc}
                    key={showFallback ? 'fallback' : effectiveSrc}
                    onLoad={showFallback ? undefined : handleLoad}
                    onError={showFallback ? undefined : handleError}
                    style={{
                        ...style,
                        visibility: isLoading ? 'hidden' : 'visible',
                    }}
                />
            )}
        </figure>
    );
}

export default MediaImage;
